import { Button, Divider, Space, Tooltip, Typography } from "antd";
import {
  parseDuration,
  addDurationTo,
  displayReservationPolicy,
} from "~/utils";

import * as APITypes from "~/API";
import {
  PrecomputedAmenityData,
  RecurringScheduleItems,
  ReservationListFiltersType,
  ScheduleItemList,
} from "~/types";
import { CustomTextField } from "~/components";
import { AmenitySchedule, ReservationTable } from "./components";
import { ReservationRules } from "../components";
import { useAppTranslation } from "~/hooks/useAppTranslation";

/**
 * PhotoProvider Docs: https://react-photo-view.vercel.app/en-US/docs/api
 */

export const AmenityReservationTab = ({
  amenityData,
  scheduleItems,
  recurringScheduleItems,
  filters,
  tenantID,
}: {
  amenityData: PrecomputedAmenityData;
  scheduleItems: ScheduleItemList[];
  recurringScheduleItems: RecurringScheduleItems[];
  filters: ReservationListFiltersType;
  tenantID: string;
}) => {
  const { t } = useAppTranslation();
  const amenityDetails = amenityData.formattedAmenityDetails.amenityDetails;
  const reservations = amenityData.formattedAmenityDetails.reservations;
  const reservationRule = amenityDetails.reservationRule;
  const duration = parseDuration(reservationRule.closestTime ?? "");
  const startWeek = duration ? addDurationTo({ duration }) : undefined;

  const atMaxRequests = reservationRule.maxRequests
    ? reservations.filter(
        (reservation) => reservation.data.tenantID === tenantID
      ).length >= reservationRule.maxRequests
    : false;

  return (
    <>
      <Space direction="vertical">
        {reservationRule.policy !==
          APITypes.AmenityReservationPolicyOptions.NotAllowed && (
          <Tooltip
            title={atMaxRequests ? t("Max_Allowed_Request_Reached") : ""}
          >
            <Button
              type="primary"
              href={`/amenity/${amenityDetails.id}/reservation`}
              disabled={atMaxRequests}
            >
              {reservationRule.requiresApproval
                ? t("Request_Reservation")
                : t("Make_Reservation")}
            </Button>
          </Tooltip>
        )}
        <Typography.Title level={3}>{t("Rules")}</Typography.Title>
        <Typography.Text strong style={{ marginBottom: 5 }}>
          {t("Policy")}
        </Typography.Text>
        <CustomTextField
          value={displayReservationPolicy(reservationRule.policy)}
        />
      </Space>

      <Divider />
      {reservationRule.policy !==
        APITypes.AmenityReservationPolicyOptions.NotAllowed && (
        <>
          <ReservationRules reservationRule={reservationRule} />
          <Divider />
          <ReservationTable
            reservations={amenityData.formattedAmenityDetails.reservations.filter(
              (value) => {
                return value.data.tenantID === tenantID;
              }
            )}
            filters={filters}
          />
          <br />
          <AmenitySchedule
            scheduleItems={scheduleItems}
            recurringScheduleItems={recurringScheduleItems}
            startWeek={startWeek}
          />
        </>
      )}
    </>
  );
};
